/**
 * AB#650 — the selected blueprint's workbench: Fonts & Colors | Standard Files |
 * Custom Files.
 *
 * All three tabs edit the SAME file source (site fork or account theme), so the
 * editor state lives here once. The parent remounts this component with a `key`
 * when the selection changes, which is the whole reset story.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useBlocker } from '@tanstack/react-router';
import { toast } from 'sonner';
import { PencilRuler, Sparkles, Trash2 } from 'lucide-react';
import type { ThemeFile } from '@wordpuppi/shared';
import { TeraEditor } from '@/components/TeraEditor';
import { ThemeStyleEditor } from '@/components/ThemeStyleEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useBaxterChanged } from '@/lib/baxterEffects';
import { BaxterChangedBar } from '@/components/baxter/BaxterChangedBar';
import { confirmDialog } from '@/lib/dialog';
import {
  splitFiles,
  styleFilePath,
  useSourceFile,
  useSourceSaveFile,
  useSourceDeleteFile,
  type Blueprint,
} from './blueprint';
import { THEME_SAMPLE_PATH } from './previewRoute';

const errorText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

/** Group files by their directory, so templates/, templates/partials/, static/css/ … each get a heading. */
function groupByDir(files: ThemeFile[]): [string, ThemeFile[]][] {
  const groups = new Map<string, ThemeFile[]>();
  for (const file of [...files].sort((a, b) => a.path.localeCompare(b.path))) {
    const dir = file.path.includes('/') ? file.path.slice(0, file.path.lastIndexOf('/')) : '/';
    const bucket = groups.get(dir);
    if (bucket) bucket.push(file);
    else groups.set(dir, [file]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

const fileName = (path: string) => path.slice(path.lastIndexOf('/') + 1);

const TAB_LIST = 'h-auto w-full justify-start gap-4 rounded-none border-b bg-transparent p-0';

/**
 * The indicator is an INSET BOX-SHADOW, not a border: a `border-bottom-color`
 * here lost the cascade to the base `*` border-color reset in the real DOM
 * (computed `--border`, not `--primary`) even though twMerge kept the class.
 * Shadow also means no border box to size, so nothing shifts between states —
 * both states carry the same shadow, transparent when inactive.
 */
const TAB_TRIGGER = [
  'h-auto flex-none rounded-none border-0 px-1 pb-2',
  'shadow-[inset_0_-2px_0_0_transparent]',
  'text-muted-foreground hover:text-foreground dark:text-muted-foreground',
  'data-[active]:bg-transparent data-[active]:text-primary',
  // `!` is load-bearing: tailwind-merge does NOT drop the shared trigger's
  // `data-[active]:shadow-sm` against an important-flagged class, so both reach
  // the DOM — and shadow-sm's rule is emitted AFTER this one at equal
  // specificity, so without the flag source order would hand it the cascade.
  'data-[active]:shadow-[inset_0_-2px_0_0_var(--color-primary)]!',
  // ponytail: no `dark:` twin — the shared trigger has no dark shadow rule to
  // out-rank (only dark text/bg/border). Add one if that changes.
  'dark:data-[active]:bg-transparent dark:data-[active]:text-primary',
].join(' ');

export function BlueprintTabs({
  blueprint,
  siteSlug,
  onDirtyPathChange,
  onSelectedPathChange,
  onSaved,
}: {
  blueprint: Blueprint;
  siteSlug: string;
  /** Reported up so "Build preview" can warn that unsaved edits aren't in the build. */
  onDirtyPathChange: (path: string | null) => void;
  /** AB#661: reported up so the preview can follow the open template. */
  onSelectedPathChange: (path: string | null) => void;
  /** A file landed on the server (saved or created) — the preview rebuilds on this. */
  onSaved?: () => void;
}) {
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [expectedHash, setExpectedHash] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [newFileOpen, setNewFileOpen] = useState(false);
  const [newFilePath, setNewFilePath] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [tab, setTab] = useState('style');

  const source = blueprint.source;
  // AB#644: a stock theme ships with WordPuppi — browsable, never editable.
  // There is no write endpoint behind it, so the UI hides every write affordance
  // rather than letting one fire a request that cannot succeed.
  const readOnly = !!blueprint.stock;
  /** The pinned "Theme sample" entry — a preview target, not a file to fetch or save. */
  const isSample = selectedPath === THEME_SAMPLE_PATH;
  const fileQuery = useSourceFile(source, isSample ? null : selectedPath);
  const saveFile = useSourceSaveFile(source);
  const deleteFile = useSourceDeleteFile(source);

  const loaded = fileQuery.data?.data;
  const isBinary = loaded?.encoding === 'base64';
  const isHtml = !!selectedPath && selectedPath.endsWith('.html');
  const { standard, custom } = splitFiles(blueprint);
  /** AB#845: standard files this blueprint has since changed — badged in the list. */
  const modified = new Set(blueprint.accountTheme?.modified_files ?? []);
  // Standard files are inherited from the theme this blueprint was copied from —
  // deleting them isn't ours to offer. FILE-scoped, not tab-scoped: the open file
  // stays open across tab switches, so "which tab am I on" is the wrong question.
  const isStandardFile = !!selectedPath && blueprint.originFiles.includes(selectedPath);

  // AB#624: guided colors/fonts editor over this blueprint's main stylesheet.
  const stylePath = styleFilePath(blueprint);

  // AB#675: read `dirty` in the load effect WITHOUT putting it in the deps — a
  // save sets it false and the effect would then re-copy the pre-save `loaded`
  // over the draft. Only a genuinely new fetch result may touch the draft.
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  // Load fetched content into the draft (only for the file that is actually selected).
  useEffect(() => {
    if (loaded && loaded.path === selectedPath) {
      // A refetch used to clobber unsaved edits (Baxter's invalidate is one).
      if (dirtyRef.current) return;
      setDraft(loaded.content);
      setExpectedHash(loaded.content_sha256);
      setDirty(false);
    }
  }, [loaded, selectedPath]);

  // AB#675: Baxter wrote a theme file. Clean editor: the refetch above lands it
  // silently. Dirty editor: keep the edits and offer a reload.
  // Scoped to the OPEN file: a write to some other template is the file tree's
  // business, not this editor's. A fork/discard names no path and still lands here.
  const baxterAt = useBaxterChanged('theme', selectedPath ?? undefined);
  const seenBaxter = useRef<number | null>(null);
  const [changedBar, setChangedBar] = useState(false);
  useEffect(() => {
    if (baxterAt == null || baxterAt === seenBaxter.current) return;
    seenBaxter.current = baxterAt;
    if (dirtyRef.current) setChangedBar(true);
  }, [baxterAt]);
  const reloadFile = async () => {
    const fresh = await fileQuery.refetch();
    const file = fresh.data?.data;
    if (file && file.path === selectedPath) {
      setDraft(file.content);
      setExpectedHash(file.content_sha256);
      setDirty(false);
    }
    setChangedBar(false);
  };

  useEffect(() => {
    onDirtyPathChange(dirty ? selectedPath : null);
  }, [dirty, selectedPath, onDirtyPathChange]);

  useEffect(() => {
    onSelectedPathChange(selectedPath);
  }, [selectedPath, onSelectedPathChange]);

  // Guard route exit (and tab close) while there are unsaved edits. A native
  // confirm() can't gate this — it's a SYNC blocker API and, under Tauri v2,
  // window.confirm() never renders (AB#505), so it would permanently trap
  // navigation. Use the resolver pattern: block on `dirty`, then let an in-app
  // dialog call proceed()/reset() (works identically on web and desktop).
  // Selecting another blueprint is a search-param navigation, so it is guarded too.
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  const selectFile = async (path: string) => {
    if (path === selectedPath) return;
    if (dirty && !(await confirmDialog(`Discard unsaved changes to ${selectedPath}?`, { destructive: true, confirmLabel: 'Discard changes' }))) return;
    setSelectedPath(path);
    setDraft('');
    setExpectedHash(null);
    setDirty(false);
    setFileError(null);
  };

  // isSample guards the FUNCTION, not just the button — Cmd/Ctrl+S calls this
  // directly and the sample has no file behind it.
  const handleSave = async () => {
    if (!selectedPath || isSample || isBinary) return;
    try {
      if (!expectedHash) throw new Error('Reload this file before saving.');
      const result = await saveFile.mutateAsync({ path: selectedPath, content: draft, expectedContent: expectedHash });
      if (!result.data?.content_sha256) throw new Error('Saved file version was not confirmed. Reload before saving again.');
      setExpectedHash(result.data.content_sha256);
      setDirty(false);
      setFileError(null);
      onSaved?.();
      toast.success('Saved');
    } catch (err) {
      // 422 carries the Tera parse error (template name + line) — show it, don't bury it.
      setFileError(errorText(err));
      // AB#941: a stale write is easy to miss as a line under the editor.
      if ((err as { data?: { code?: string } })?.data?.code === 'THEME_CONFLICT') {
        toast.error('Not saved: this file changed since you opened it', { description: 'Copy your edits, reload the file, then apply them again.' });
      }
    }
  };

  // Cmd/Ctrl+S saves the open file.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const handleCreateFile = async () => {
    const path = newFilePath.trim().replace(/^\/+/, '');
    if (!path) return;
    try {
      await saveFile.mutateAsync({ path, content: '', expectedContent: '' });
      setNewFileOpen(false);
      setNewFilePath('');
      setSelectedPath(path);
      setDraft('');
      setDirty(false);
      setFileError(null);
      onSaved?.();
      toast.success('File created');
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const handleDeleteFile = async () => {
    if (!selectedPath) return;
    try {
      await deleteFile.mutateAsync(selectedPath);
      setDeleteOpen(false);
      setSelectedPath(null);
      setDraft('');
      setDirty(false);
      setFileError(null);
      toast.success('File deleted');
    } catch (err) {
      // 422 = the delete would break the template parse.
      setDeleteOpen(false);
      setFileError(errorText(err));
    }
  };

  /** Tree + editor. Both file tabs render this over their own slice of the files. */
  const workbench = (
    files: ThemeFile[],
    opts: { allowNew?: boolean; sample?: boolean; empty: string },
  ) => (
    <div className="flex flex-col gap-4 @xl:flex-row @xl:items-start">
      <div className="w-full shrink-0 overflow-y-auto rounded-lg border p-2 max-h-[40vh] @xl:max-h-[60vh] @xl:w-56">
        {opts.sample && (
          <button
            onClick={() => selectFile(THEME_SAMPLE_PATH)}
            className={cn(
              'mb-3 flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm min-h-11 md:min-h-0 transition-colors hover:bg-accent',
              isSample && 'bg-accent font-medium text-foreground',
            )}
            title="A canned sample page rendered through this blueprint"
          >
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1 truncate">Theme sample</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[0.625rem] font-normal uppercase tracking-wide text-muted-foreground">
              sample
            </span>
          </button>
        )}
        {opts.allowNew && !readOnly && (
          <div className="mb-1 flex items-center justify-between gap-2 border-b px-2 pb-2">
            <span className="text-xs font-medium text-muted-foreground">Your files</span>
            <Button variant="ghost" size="sm" onClick={() => setNewFileOpen(true)}>
              New file
            </Button>
          </div>
        )}
        {files.length === 0 ? (
          <p className="p-2 text-sm text-muted-foreground">{opts.empty}</p>
        ) : (
          groupByDir(files).map(([dir, group]) => (
            <div key={dir} className="mb-3">
              <div className="px-2 py-1 font-mono text-xs font-medium text-muted-foreground">
                {dir}/
              </div>
              {group.map((file) => (
                <div key={file.path} className="group flex items-center gap-1">
                  <button
                    onClick={() => selectFile(file.path)}
                    className={cn(
                      'min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left font-mono text-sm min-h-11 md:min-h-0 transition-colors hover:bg-accent',
                      selectedPath === file.path && 'bg-accent font-medium text-foreground',
                    )}
                    title={file.path}
                  >
                    {fileName(file.path)}
                  </button>
                  {/* AB#845: still a standard file — this only says you changed it. */}
                  {modified.has(file.path) && (
                    <span
                      className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[0.625rem] font-normal uppercase tracking-wide text-muted-foreground"
                      title="Edited — differs from the theme this was copied from"
                    >
                      edited
                    </span>
                  )}
                  {/* AB#812: design this template on the canvas instead of in code.
                      A stock theme has nothing to write to — duplicate it first,
                      the same answer its editor gives. */}
                  {file.path.endsWith('.html') &&
                    (readOnly ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        disabled
                        aria-label={`Design ${fileName(file.path)}`}
                        title="Duplicate this stock theme to design its templates"
                      >
                        <PencilRuler className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        // Hover-revealed on pointer devices; always visible where
                        // there is no hover to reveal it with.
                        className="transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
                        asChild
                        title="Design on the canvas"
                      >
                        <Link
                          to="/sites/$siteSlug/theme/design"
                          params={{ siteSlug }}
                          search={{ bp: blueprint.key, path: file.path }}
                          aria-label={`Design ${fileName(file.path)}`}
                        >
                          <PencilRuler className="h-4 w-4" />
                        </Link>
                      </Button>
                    ))}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="min-w-0 flex-1">
        {!selectedPath ? (
          <div className="rounded-lg border border-dashed px-6 py-16 text-center">
            <p className="text-lg font-medium">
              {readOnly ? 'Select a file to read' : 'Select a file to edit'}
            </p>
            <p className="mx-auto mt-1 max-w-sm text-muted-foreground">
              {readOnly
                ? 'This is a stock theme — read its templates here, or duplicate it to get a copy you can edit.'
                : 'Templates and static assets in this blueprint are yours — edits only affect builds that use it.'}
            </p>
          </div>
        ) : isSample ? (
          // No file, so no editor and no Save/Delete — the preview does the talking.
          <div className="rounded-lg border border-dashed px-6 py-16 text-center">
            <p className="mx-auto max-w-sm text-muted-foreground">
              A canned sample page rendered through this blueprint — not part of your site or its
              builds.
            </p>
          </div>
        ) : (
          <>
            {changedBar && (
              <BaxterChangedBar
                noun="file"
                onReload={() => void reloadFile()}
                onDismiss={() => setChangedBar(false)}
              />
            )}
            <div className="mb-2 flex items-center justify-between gap-4">
              <span className="truncate font-mono text-sm">
                {selectedPath}
                {dirty && <span className="ml-2 text-muted-foreground">• unsaved</span>}
              </span>
              {readOnly ? (
                <span className="shrink-0 text-xs text-muted-foreground">Read-only</span>
              ) : (
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={handleSave} disabled={!dirty || isBinary || saveFile.isPending}>
                    {saveFile.isPending ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setDeleteOpen(true)}
                    disabled={deleteFile.isPending || isStandardFile}
                    title={
                      isStandardFile
                        ? 'Standard files come from the theme this was copied from'
                        : 'Delete file'
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* AB#836: a fork renders this partial only where post.html includes it. */}
            {selectedPath === 'templates/partials/comments.html' && (
              <p className="mb-2 text-xs text-muted-foreground">
                Forks: this partial only renders where templates/post.html has{' '}
                {'{% include "partials/comments.html" %}'}. Add the include, build, then deploy.
                {/* AB#845: the other invisible precondition — the preview jumps to a post,
                    and without the per-post opt-in the partial renders nothing there. */}{' '}
                It also renders nothing on a post that has comments switched off (Settings →
                Comments).
              </p>
            )}
            {fileQuery.isLoading ? (
              <Skeleton className="h-96 w-full rounded-lg" />
            ) : fileQuery.error ? (
              <div className="py-12 text-center text-muted-foreground">Error loading file</div>
            ) : isBinary ? (
              <div className="rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground">
                Binary file — not editable here. Replace it from the Media → Static tab.
              </div>
            ) : isHtml && !readOnly ? (
              <TeraEditor
                content={draft}
                onChange={(value) => {
                  setDraft(value);
                  setDirty(true);
                }}
                siteSlug={siteSlug}
              />
            ) : (
              <textarea
                className="min-h-96 w-full rounded-lg border bg-background p-3 font-mono text-sm leading-6 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setDirty(true);
                }}
                spellCheck={false}
                readOnly={readOnly}
              />
            )}

            {fileError && (
              <pre className="mt-3 whitespace-pre-wrap rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
                {fileError}
              </pre>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(String(v));
          // The open file survives a tab switch (Rick, part 20) — the other tab
          // just shows it unhighlighted. Standard Files with nothing open lands
          // on the Theme sample so the pane shows something immediately.
          if (v === 'standard' && selectedPath == null) void selectFile(THEME_SAMPLE_PATH);
        }}
      >
        {/* Underline tab bar, only here: the pill look reads as inert next to
            the blueprint tiles. Base UI marks the active tab `data-active`. */}
        <TabsList className={TAB_LIST}>
          <TabsTrigger value="style" className={TAB_TRIGGER}>
            Fonts &amp; Colors
          </TabsTrigger>
          <TabsTrigger value="standard" className={TAB_TRIGGER}>
            Standard Files
          </TabsTrigger>
          <TabsTrigger value="custom" className={TAB_TRIGGER}>
            Custom Files
          </TabsTrigger>
        </TabsList>

        {/* keepMounted: the style editor holds unsaved palette edits — unmounting
            it on a tab switch would drop them silently. */}
        <TabsContent value="style" className="@container pt-3" keepMounted>
          {readOnly ? (
            <p className="rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground">
              Stock themes can&apos;t be edited. Duplicate this one to change its colors and fonts.
            </p>
          ) : stylePath ? (
            <ThemeStyleEditor source={source} stylePath={stylePath} siteSlug={siteSlug} />
          ) : (
            <p className="rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground">
              This blueprint has no stylesheet under <code>static/</code>. Add one in Custom Files
              to edit colors and fonts here.
            </p>
          )}
        </TabsContent>

        <TabsContent value="standard" className="@container pt-3">
          {workbench(standard, { sample: true, empty: 'This blueprint has no inherited files.' })}
        </TabsContent>

        <TabsContent value="custom" className="@container pt-3">
          {workbench(custom, {
            allowNew: true,
            empty: 'No files of your own yet — "New file" adds one.',
          })}
        </TabsContent>
      </Tabs>

      {/* New file */}
      <Dialog open={newFileOpen} onOpenChange={setNewFileOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New theme file</DialogTitle>
            <DialogDescription>
              Path relative to the theme root, e.g.{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                templates/partials/hero.html
              </code>
              .
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="newFilePath">File path</Label>
            <Input
              id="newFilePath"
              value={newFilePath}
              onChange={(e) => setNewFilePath(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFile();
              }}
              placeholder="templates/partials/hero.html"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setNewFileOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFile} disabled={!newFilePath.trim() || saveFile.isPending}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete file */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete theme file?</DialogTitle>
            <DialogDescription>
              This will permanently delete{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{selectedPath}</code>.
              Templates that include it will break.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteFile} disabled={deleteFile.isPending}>
              {deleteFile.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved-changes navigation guard (AB#505 — replaces window.confirm so it
          works under Tauri, where native confirm() never renders). */}
      <Dialog open={blocker.status === 'blocked'} onOpenChange={(open) => !open && blocker.reset?.()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard unsaved theme changes?</DialogTitle>
            <DialogDescription>
              You have unsaved edits to{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{selectedPath}</code>.
              Leaving this page will discard them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => blocker.reset?.()}>
              Keep editing
            </Button>
            <Button variant="destructive" onClick={() => blocker.proceed?.()}>
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
